import React, { useEffect, useRef, useState } from "react";

const SRC = "https://accounts.google.com/gsi/client";
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let loading = null;
const loadGis = () => {
    if (window.google?.accounts?.id) return Promise.resolve();
    loading = loading || new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = SRC;
        s.async = true;
        s.onload = resolve;
        s.onerror = () => { loading = null; reject(new Error("Google script blocked")); };
        document.head.appendChild(s);
    });
    return loading;
};

/**
 * Google Identity Services button. Renders nothing when
 * REACT_APP_GOOGLE_CLIENT_ID is unset, so the password form stays usable in
 * environments where Google sign-in isn't configured.
 */
export default function GoogleSignIn({ onCredential, text = "signin_with", disabled }) {
    const ref = useRef(null);
    const [failed, setFailed] = useState(false);
    const cb = useRef(onCredential);
    cb.current = onCredential;

    useEffect(() => {
        if (!CLIENT_ID || !ref.current) return;
        let cancelled = false;
        loadGis()
            .then(() => {
                if (cancelled || !ref.current) return;
                window.google.accounts.id.initialize({
                    client_id: CLIENT_ID,
                    callback: (res) => cb.current?.(res.credential),
                    // One-tap is easy to dismiss by accident and then never
                    // comes back; the explicit button is the reliable path.
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });
                window.google.accounts.id.renderButton(ref.current, {
                    theme: "outline", size: "large", shape: "pill",
                    text, width: 320, logo_alignment: "center",
                });
            })
            .catch(() => !cancelled && setFailed(true));
        return () => { cancelled = true; };
    }, [text]);

    if (!CLIENT_ID) return null;

    return (
        <div className="mt-5" data-testid="google-signin">
            <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-plum/10" />
                <span className="text-xs uppercase tracking-[0.15em] text-plum/40">or</span>
                <div className="h-px flex-1 bg-plum/10" />
            </div>
            {failed ? (
                <p className="text-xs text-center text-plum/50">
                    Google sign-in couldn't load. Use your email and password above.
                </p>
            ) : (
                <div ref={ref} className={`flex justify-center ${disabled ? "opacity-60 pointer-events-none" : ""}`} />
            )}
        </div>
    );
}
